import { TAO_KOE, EN_KO, TaoStorage, TaoConsole, TaoPromise, Render } from './../libs';

import renderSubmenu from './templates/submenu-mobile.tpl.html';

const getLinkProps = ( link ) => {
	const res = {};

	if ( link && ( link.url || link.href ) ) {
		res.href = link.url || link.href;
	} else {
		res.href = '#';
	}

	if ( link && link.target ) {
		res.target = link.target;
	}

	if ( link && link.title ) {
		res.title = link.title;
	}

	return Object.keys( res ).map( ( key ) => {
		const val = res[ key ];
		return `${ key }="${ val }"`;
	} ).join( ' ' );
};

class MenuMobile {
	constructor( element ) {
		this.element = element;
		this.backdrop = element.querySelector( '.mdl_menu_mobile_backdrop' );
		this.content = element.querySelector( '.mdl_menu_mobile__content' );
		this.titleEl = element.querySelector( '.mdl_menu_mobile__header-title' );
		this.backbutton = element.querySelector( '.mdl_menu_mobile__back' );
		this.navElement = element.querySelector( '.mdl_menu_mobile__nav' );

		this.subMenus = [];

		// Add Observer to Menu Parent Element to check for is-open
		this.addObserver( 'is-open' );

		// Add Close Events.
		this.element.querySelectorAll( '.js_menu_mobile__close, .js_menu_mobile_backdrop__close' ).forEach( ( closer ) => closer.addEventListener( 'click', () => this.close() ) );

		// Add Click Events.
		this.element.querySelectorAll( '.mdl_menu_mobile__item-link' ).forEach( ( linkEl ) => linkEl.addEventListener( 'click', ( ev ) => this.onClick( ev, linkEl ) ) );

		// Add Back Events.
		this.backbutton.addEventListener( 'click', () => this.onClickBack() );
	}

	open = () => {
		// Open Menu.
		this.element.classList.add( 'is-open' );
	};

	close = () => {
		// Close Menu.
		this.element.classList.remove( 'is-open' );
	};

	addObserver = ( className ) => {
		// Observer checks when the Menu Opens or closes.
		this.observer = new MutationObserver( ( mutationList ) => {
			for ( const mutation of mutationList ) {
				if ( 'attributes' !== mutation.type ) {
					continue;
				}
				if ( 'class' !== mutation.attributeName ) {
					continue;
				}

				const oldVal = mutation.oldValue.includes( className );
				const newVal = mutation.target.classList.contains( className );

				// Has Changed.
				if ( oldVal !== newVal ) {
					if ( newVal ) {
						this.onOpen();
					} else {
						this.onClose();
					}
				}
			}
		} );

		this.observer.observe( this.element, {
			attributes: true,
			attributeOldValue: true,
			childList: false,
			subtree: false,
		} );
	};

	onOpen = () => {
		this.backdrop.classList.add( 'is-visible' );
		EN_KO.functions.disablePageScroll( this.content );

		MenuMobile.preload();
	};

	onClose = () => {
		this.backdrop.classList.remove( 'is-visible' );
		EN_KO.functions.enablePageScroll( this.content );
	};

	onClickBack = () => {
		if ( this.isLoading ) {
			return true;
		}

		this.popSubmenu();
	};

	onClick = ( ev, link ) => {
		// Get Links Parent li element.
		const parentEl = link.parentElement;

		// Element has no Submenu - We handle like a normal click.
		if ( ! parentEl.classList.contains( 'mdl_menu_mobile__item--has-submenu' ) ) {
			return true;
		}

		ev.preventDefault();
		if ( this.isLoading ) {
			return false;
		}

		this.setLoading( true );
		MenuMobile.preload().then( async( { menu } ) => {
			const menuId = parentEl.dataset.menu;
			const data = MenuMobile.prepareData( menu[ menuId ] );

			const SubmenuItem = new SubmenuMobile( data, link.innerText, 1 );
			this.pushSubmenu( await SubmenuItem );
		} ).catch( () => {
			// When load or anything else breaks we free the menu.
			this.setLoading( false );
		} );
	};

	pushSubmenu = ( subMenu ) => {
		// Get Menu.
		this.subMenus.push( subMenu );
		this.setLoading( true );
		this.navElement.appendChild( subMenu.element );

		// Toggle submenu class after transition.
		subMenu.awaitNextTransition().then( () => {
			this.toggleSubmenuDisplay();
			this.setLoading( false );
		} );

		// Wait for append to
		subMenu.show();
		this.onMenuSwitch();
	};

	popSubmenu = () => {
		// Get Menu.
		const subMenu = this.subMenus.pop();
		this.setLoading( true );

		// Remove NavElement after the Transition completes.
		subMenu.awaitNextTransition().then( () => {
			this.navElement.removeChild( subMenu.element );
			this.setLoading( false );
		} );

		// Start transition.
		this.toggleSubmenuDisplay();

		subMenu.hide();
		this.onMenuSwitch();
	};

	toggleSubmenuDisplay = () => {
		// Get currently active submenu.
		const currentSubmenu = this.subMenus[ this.subMenus.length - 1 ];

		// Add class to root, if there are elements, and hide all subMenus, except the newest.
		this.navElement.classList.toggle( 'is-submenu-open', this.subMenus.length > 0 );
		this.subMenus.forEach( ( subMenu ) => subMenu.element.classList.toggle( 'en_hidden', subMenu !== currentSubmenu ) );
	};

	onMenuSwitch = () => {
		this.backbutton.classList.toggle( 'is-visible', this.subMenus.length > 0 );

		if ( this.subMenus.length > 0 ) {
			this.titleEl.innerHTML = this.subMenus[ this.subMenus.length - 1 ].getTitle();
		} else {
			this.titleEl.innerHTML = 'Menü';
		}
	};

	isLoading = false;
	setLoading = ( loading ) => {
		this.isLoading = loading;
	};

	static promise;
	static preload = () => {
		if ( this.promise ) {
			return this.promise;
		}

		// Get Local Storage Instance.
		const Storage = TaoStorage.getLocalStorage();

		try {
			// Try to get data from LocalStorage.
			const time = parseInt( Storage.getItem( 'menu_mobile_v3_time' ) );
			const menu = JSON.parse( Storage.getItem( 'menu_mobile_v3_data' ) );

			if ( menu && time && time >= TAO_KOE?.MenuTimestamp ) {
				return Promise.resolve( {
					time,
					menu,
				} );
			}
		} catch ( err ) {
			TaoConsole.error( err );
		}

		// Load Data from API Endpoint.
		return this.promise = fetch( '/dispatch/menu/json-mobile/' )
			.then( ( response ) => response.json() )
			.then( ( response ) => {
				Storage.setItem( 'menu_mobile_v3_time', response.time );
				Storage.setItem( 'menu_mobile_v3_data', JSON.stringify( response.menu ) );
				return response;
			} );
	};

	static prepareData = ( data ) => {
		return {
			all: false,
			...data,
			item: false,
		};
	};
}

class SubmenuMobile {
	isVisible = false;

	level = 1;

	constructor( data, title = 'Menü', level = 1 ) {
		// Base Data.
		this.data = data;
		this.title = title;
		this.level = level;

		return Promise.resolve()
			.then( async() => {
				// Create Element.
				this.element = document.createElement( 'div' );
				this.element.className = `mdl_menu_mobile__nav-sub mdl_menu_mobile__nav-sub-level${ level }`;
				this.element.innerHTML = await Render.render( 'submenu-mobile', data );

				// Fire Menuswitch event, when the transition ends.
				this.element.addEventListener( 'transitionend', () => this.onTransitionEnd() );

				// Add Click Events.
				this.element.querySelectorAll( '.mdl_menu_mobile__item-link' ).forEach( ( linkEl ) => linkEl.addEventListener( 'click', ( ev ) => this.onClick( ev, linkEl ) ) );

				return this;
			} );
	}

	show = () => {
		this.toggle( true );
	};

	hide = () => {
		this.toggle( false );
	};

	toggle = ( isVisible ) => {
		// Force style recalculation before we change submenu state.
		this.element.getBoundingClientRect();

		// Update Submenu state.
		this.isVisible = isVisible;
		this.element.classList.toggle( 'is-visible', this.isVisible );
	};

	awaitNextTransition = () => {
		return TaoPromise.timeout(
			( resolve ) => {
				const resolver = () => resolve();

				this.element.addEventListener( 'menuswitch.tao', resolver );
				return () => this.element.removeEventListener( 'menuswitch.tao', resolver );
			},
			400,
		);
	};

	onTransitionEnd = () => {
		// Dispatch Event for Menuswitch.
		this.element.dispatchEvent( new CustomEvent( 'menuswitch.tao', { bubbles: true } ) );
	};

	onClick = ( ev, link ) => {
		// Get Links Parent li element.
		const parentEl = link.parentElement;

		// Element has no Submenu - We handle like a normal click.
		if ( ! parentEl.classList.contains( 'mdl_menu_mobile__item--has-submenu' ) ) {
			return true;
		}

		ev.preventDefault();
		if ( window.TaoMenuMobile.isLoading ) {
			return false;
		}

		// Get Data.
		const menuIds = ( '' + parentEl.dataset.menuIndex ).split( '-' );
		const data = menuIds.reduce( ( curr, menuId ) => {
			if ( curr.menu && curr.menu[ menuId ] ) {
				return MenuMobile.prepareData( curr.menu[ menuId ] );
			}

			return curr;
		}, this.data );

		const SubmenuPromise = new SubmenuMobile( data, link.innerText, this.level + 1 );
		SubmenuPromise.then( ( SubmenuItem ) => {
			window.TaoMenuMobile.pushSubmenu( SubmenuItem );
		} );
	};

	getTitle = () => {
		return this.title;
	};
}

// Init.
TAO_KOE.ready( 'menu-mobile.js', function() {
	if ( TAO_KOE.MenuDisable ) {
		return;
	}

	const menuElement = document.getElementById( 'menu_mobile' );
	if ( ! menuElement ) {
		return;
	}

	// Add Template to render submenu.
	Render.addTemplate( 'submenu-mobile', ( data ) => renderSubmenu( { getLinkProps, ...data } ) );

	// Create MenuMobile Instance.
	window.TaoMenuMobile = new MenuMobile( menuElement );
} );

export default MenuMobile;
